import React, { useState, useEffect } from 'react';
import dbService from '../../../services/dbService';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { Button, Spinner, Tabs, Tab } from 'react-bootstrap';
import MappingTable from './MappingTable';
import PageContainer from '../../layout/PageContainer';

// Mock source data examples to use in the live preview
const MOCK_KYC_SOURCE = {
    "CUST_FNAME": "John",
    "CUST_LNAME": "Doe",
    "DOB": "1985-05-20",
    "ENTITY_TYPE": "IND",
    "ADDR_LINE1": "123 Maple St",
    "ADDR_CITY": "Anytown",
    "ADDR_STATE": "CA",
    "ADDR_ZIP": "12345",
    "ADDR_COUNTRY": "USA",
    "NATIONALITY": "USA",
    "OCCUPATION_CD": "Used Car Dealer",
    "ANNUAL_INCOME": 95000,
    "SOURCE_OF_WEALTH": "Business Income"
};

const MOCK_TRANSACTION_SOURCE = {
    "TRN_DATE": "2025-08-28",
    "CUST_ID": "CUST-101",
    "TRN_AMT_LCY": 15000.00,
    "TRN_TYPE_CD": "WIR",
    "BEN_NAME": "Global Export Co.",
    "BEN_BANK": "International Bank of Commerce",
    "BENEFICIARY_COUNTRY": "SYR",
    "MEMO": "Payment for used auto parts"
};



const DataMapping = () => {
    const [kycMapping, setKycMapping] = useState(null);
    const [transactionMapping, setTransactionMapping] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            const [kycData, txnData] = await Promise.all([
                dbService.getById('mappings', 'kyc'),
                dbService.getById('mappings', 'transaction'),
            ]);
            setKycMapping(kycData);
            setTransactionMapping(txnData);
            setIsLoading(false);
        };
        fetchData();
    }, []);

    const handleKycMapChange = (sourceField, targetField) => {
        setKycMapping(prev => ({
            ...prev,
            currentMap: { ...prev.currentMap, [sourceField]: targetField }
        }));
    };
    
    const handleTransactionMapChange = (sourceField, targetField) => {
        setTransactionMapping(prev => ({
            ...prev,
            currentMap: { ...prev.currentMap, [sourceField]: targetField }
        }));
    };

    const handleSaveChanges = async () => {
        setIsSaving(true);
        try {
            await Promise.all([
                dbService.update('mappings', kycMapping),
                dbService.update('mappings', transactionMapping)
            ]);
            await dbService.addLog({ user: user.name, action: 'Data Mappings Updated', details: 'KYC and Transaction mappings were saved.' });
            toast.success("Data mappings saved successfully!");
        } catch (error) {
            toast.error("Failed to save mappings.");
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) return <div className="text-center p-5"><Spinner animation="border" /></div>;

    return (
        <PageContainer title="Data Mapping Studio">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2></h2>
                <Button variant="primary" onClick={handleSaveChanges} disabled={isSaving}>
                    {isSaving ? <Spinner as="span" size="sm" /> : 'Save All Mappings'}
                </Button>
            </div>
            <p>
                Configure how raw data from source systems (like a Core Banking System) is transformed into the clean, standardized format required by the AML engine.
                Your selections are saved and will persist.
            </p>

            <Tabs defaultActiveKey="kyc" id="mapping-tabs" className="mt-3">
                <Tab eventKey="kyc" title="👤 KYC Data Mapping">
                    <div className="p-3 border border-top-0">
                        {kycMapping && (
                            <MappingTable 
                                mappingConfig={kycMapping}
                                onMapChange={handleKycMapChange}
                                sourceDataPreview={MOCK_KYC_SOURCE}
                            />
                        )}
                    </div>
                </Tab>
                <Tab eventKey="transaction" title="💳 Transaction Data Mapping">
                    <div className="p-3 border border-top-0">
                         {transactionMapping && (
                            <MappingTable 
                                mappingConfig={transactionMapping}
                                onMapChange={handleTransactionMapChange}
                                sourceDataPreview={MOCK_TRANSACTION_SOURCE}
                            />
                        )}
                    </div>
                </Tab>
            </Tabs>
        </PageContainer>
    );
};

export default DataMapping;